import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import useFetch from "../../../hook/useFetch";
import useHeaderRefresh from "../../../hook/useHeaderRefresh";

const newKey = () => {
  if (window.crypto.randomUUID) return window.crypto.randomUUID();
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const emptyHistoryFilters = { recipient: "", subject: "", status: "", from: "", to: "" };

export default function AdminMail() {
  const location = useLocation();
  const { get, post, update, del } = useFetch();
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyFilters, setHistoryFilters] = useState(emptyHistoryFilters);
  const [appliedHistoryFilters, setAppliedHistoryFilters] = useState(emptyHistoryFilters);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyMeta, setHistoryMeta] = useState({ total: 0, page: 1, pages: 1 });
  const [historyLoading, setHistoryLoading] = useState(false);
  const historyRequestId = useRef(0);
  const [selectedIds, setSelectedIds] = useState(() => [...new Set(location.state?.userIds || [])].map(Number));
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [reviewUrl, setReviewUrl] = useState("");
  const [format, setFormat] = useState("text");
  const [templateId, setTemplateId] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [tab, setTab] = useState("compose");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [requestKey, setRequestKey] = useState(newKey);

  const loadTemplates = () => get("/admin/mail-templates", null, error => setMessage(String(error)), data => setTemplates(Array.isArray(data) ? data : []));
  const loadHistory = (filters = appliedHistoryFilters, page = historyPage) => {
    const requestId = ++historyRequestId.current;
    setHistoryLoading(true);
    return get("/admin/mail-history", { ...filters, page }, error => {
      if (requestId !== historyRequestId.current) return;
      setHistoryLoading(false);
      setMessage(String(error));
    }, data => {
      if (requestId !== historyRequestId.current) return;
      setHistoryLoading(false);
      setHistory(Array.isArray(data?.rows) ? data.rows : []);
      setHistoryMeta({ total: Number(data?.total || 0), page: Number(data?.page || 1), pages: Number(data?.pages || 1) });
      setHistoryPage(Number(data?.page || 1));
    }, error => {
      if (requestId !== historyRequestId.current) return;
      setHistoryLoading(false);
      setMessage(String(error));
    });
  };

  const applyHistoryFilters = event => {
    event.preventDefault();
    setAppliedHistoryFilters({ ...historyFilters });
    setHistoryPage(1);
    loadHistory(historyFilters, 1);
  };

  const resetHistoryFilters = () => {
    setHistoryFilters(emptyHistoryFilters);
    setAppliedHistoryFilters(emptyHistoryFilters);
    setHistoryPage(1);
    loadHistory(emptyHistoryFilters, 1);
  };

  useEffect(() => {
    get("/admin/users", null, error => setMessage(String(error)), data => setUsers(Array.isArray(data) ? data : Object.values(data || {})));
    loadTemplates();
    loadHistory();
  }, []);
  useHeaderRefresh(() => {
    setMessage("");
    return Promise.all([
      get("/admin/users", null, error => setMessage(String(error)), data => setUsers(Array.isArray(data) ? data : Object.values(data || {}))),
      loadTemplates(),
      loadHistory(appliedHistoryFilters, historyPage),
    ]);
  });

  const selectedUsers = useMemo(() => users.filter(user => selectedIds.includes(Number(user.id))), [users, selectedIds]);
  const validRecipients = selectedIds.length > 0 && selectedIds.length <= 50 && selectedUsers.length === selectedIds.length;
  const canSend = validRecipients && subject.trim() && body.trim()
    && (!body.includes("{{review_url}}") || reviewUrl.trim()) && !busy;

  const selectTemplate = id => {
    const template = templates.find(item => String(item.id) === String(id));
    setTemplateId(id);
    if (template) {
      setTemplateName(template.name);
      setSubject(template.subject);
      setBody(template.body);
      setFormat(template.format);
    } else {
      setTemplateName("");
    }
  };

  const saveTemplate = () => {
    if (!templateName.trim() || !subject.trim() || !body.trim()) {
      setMessage("Vorlagenname, Betreff und Inhalt sind erforderlich.");
      return;
    }
    setBusy(true);
    const payload = { name: templateName, subject, body, format };
    const onError = error => { setBusy(false); setMessage(String(error)); };
    const onSaved = data => {
      setBusy(false);
      setTemplateId(String(data.id));
      setMessage("Vorlage gespeichert.");
      loadTemplates();
    };
    if (templateId) update("/admin/mail-template", { ...payload, id: templateId }, onError, onSaved, onError);
    else post("/admin/mail-template", payload, onError, onSaved, onError);
  };

  const removeTemplate = () => {
    if (!templateId) return;
    del("/admin/mail-template", { id: templateId }, error => setMessage(String(error)), () => {
      setTemplateId("");
      setTemplateName("");
      setMessage("Vorlage gelöscht.");
      loadTemplates();
    });
  };

  const send = () => {
    if (!canSend || !window.confirm(`E-Mail an ${selectedIds.length} ausgewählte Nutzer jetzt senden?`)) return;
    setBusy(true);
    setMessage("");
    const onError = error => { setBusy(false); setMessage(`Versand nicht bestätigt: ${String(error)}. Bitte zuerst den Verlauf prüfen, bevor du erneut sendest.`); };
    post("/admin/mail-send", {
      user_ids: JSON.stringify(selectedIds), subject, body, format,
      template_id: templateId || null, request_key: requestKey,
      review_url: reviewUrl.trim() || null,
    }, onError, data => {
      setBusy(false);
      const rows = data.rows || [];
      const sent = rows.filter(row => row.status === "sent").length;
      const failed = rows.filter(row => row.status === "failed").length;
      setMessage(data.duplicate
        ? "Dieser Versand wurde bereits verarbeitet. Prüfe den Verlauf."
        : `${sent} E-Mail(s) gesendet, ${failed} fehlgeschlagen.`);
      setSelectedIds([]);
      setRequestKey(newKey());
      loadHistory();
      setTab("history");
    }, onError);
  };

  return <main className="container-fluid py-3 admin-mail">
    <header className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-4">
      <div><h1>Admin-E-Mails</h1><p className="text-muted mb-0">Individuelle Nachrichten an ausgewählte Nutzer, Vorlagen und Versandverlauf.</p></div>
      <Link to="/admin/users" className="btn btn-outline-secondary">Nutzer auswählen</Link>
    </header>
    {message && <div className="alert alert-info" role="status">{message}</div>}
    <div className="btn-group mb-4" role="tablist" aria-label="E-Mail-Verwaltung">
      <button type="button" className={`btn ${tab === "compose" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setTab("compose")}>Verfassen</button>
      <button type="button" className={`btn ${tab === "history" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => { setTab("history"); loadHistory(); }}>Versandverlauf</button>
    </div>
    {tab === "compose" ? <div className="row g-4">
      <section className="col-xl-7" aria-label="E-Mail verfassen"><div className="card shadow-sm p-4">
        <h2 className="h5">Empfänger ({selectedIds.length})</h2>
        {selectedUsers.length ? <div className="d-flex flex-wrap gap-2 mb-3">{selectedUsers.map(user => <span className="badge bg-light text-dark border" key={user.id}>{user.username} &lt;{user.email}&gt; <button type="button" className="btn-close btn-close-sm ms-2" aria-label={`${user.username} entfernen`} onClick={() => setSelectedIds(ids => ids.filter(id => id !== Number(user.id)))} /></span>)}</div>
          : <p className="text-muted">Bitte zuerst in der Benutzerliste einen oder mehrere Nutzer auswählen.</p>}
        {selectedIds.length > 50 && <p className="text-danger">Maximal 50 Empfänger pro Versand.</p>}
        <label className="form-label" htmlFor="admin-mail-subject">Betreff</label>
        <input id="admin-mail-subject" className="form-control mb-3" maxLength={255} value={subject} onChange={event => setSubject(event.target.value)} />
        <label className="form-label" htmlFor="admin-mail-format">Format</label>
        <select id="admin-mail-format" className="form-select mb-3" value={format} onChange={event => setFormat(event.target.value)}><option value="text">Reiner Text</option><option value="html">HTML</option></select>
        <label className="form-label" htmlFor="admin-mail-body">Inhalt</label>
        <textarea id="admin-mail-body" className="form-control font-monospace" rows={14} maxLength={100000} value={body} onChange={event => setBody(event.target.value)} placeholder={format === "html" ? "<p>Hallo ...</p>" : "Hallo ..."} />
        <p className="text-muted small mt-2 mb-2">Platzhalter: <code>{"{{user_name}}"}</code> und <code>{"{{review_url}}"}</code>. Einen festen Link kannst du auch direkt im HTML bearbeiten.</p>
        <label className="form-label" htmlFor="admin-mail-review-url">Bewertungslink {body.includes("{{review_url}}") ? "(erforderlich)" : "(optional)"}</label>
        <input id="admin-mail-review-url" className="form-control" type="url" placeholder="https://raschgroup.com/bewertung/#rsg-reviews" value={reviewUrl} onChange={event => setReviewUrl(event.target.value)} />
        <div className="d-flex align-items-center gap-3 mt-3 flex-wrap"><button type="button" className="btn btn-primary" disabled={!canSend} onClick={send}>{busy ? "Bitte warten …" : `An ${selectedIds.length} Nutzer senden`}</button><small className="text-muted">Vor dem Versand erscheint eine Bestätigung.</small></div>
      </div></section>
      <aside className="col-xl-5"><div className="card shadow-sm p-4 mb-4">
        <h2 className="h5">Vorlagen aus der RSG-API</h2>
        <label className="form-label" htmlFor="admin-mail-template">Vorlage laden</label>
        <select id="admin-mail-template" className="form-select mb-3" value={templateId} onChange={event => selectTemplate(event.target.value)}><option value="">Neue Vorlage / ohne Vorlage</option>{templates.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select>
        <label className="form-label" htmlFor="admin-mail-template-name">Vorlagenname</label>
        <input id="admin-mail-template-name" className="form-control mb-3" maxLength={150} value={templateName} onChange={event => setTemplateName(event.target.value)} />
        <div className="d-flex gap-2 flex-wrap"><button type="button" className="btn btn-outline-primary" disabled={busy} onClick={saveTemplate}>{templateId ? "Vorlage aktualisieren" : "Als Vorlage speichern"}</button>{templateId && <button type="button" className="btn btn-outline-danger" onClick={removeTemplate}>Vorlage löschen</button>}</div>
        <p className="text-muted small mt-3 mb-0">HTML-Vorlagen können im Inhaltsfeld direkt bearbeitet werden. Gespeicherte Vorlagen stehen allen Admins zur Verfügung.</p>
      </div><div className="card shadow-sm p-4"><h2 className="h5">Vorschau</h2>{format === "html"
        ? <iframe title="E-Mail-Vorschau" sandbox="" srcDoc={body} style={{ width: "100%", minHeight: 260, border: "1px solid #ddd" }} />
        : <pre className="mb-0 text-wrap" style={{ whiteSpace: "pre-wrap" }}>{body || "Noch kein Inhalt."}</pre>}</div></aside>
    </div> : <section className="card shadow-sm p-4"><h2 className="h5">Versandverlauf</h2><p className="text-muted small">Nach Empfänger, Betreff, Status und Erstellungsdatum filtern. Die API liefert 25 Einträge pro Seite.</p>
      <form className="row g-3 align-items-end mb-4" onSubmit={applyHistoryFilters}>
        <div className="col-md-6 col-xl-3"><label className="form-label" htmlFor="mail-filter-recipient">Empfänger</label><input id="mail-filter-recipient" type="search" className="form-control" maxLength={100} placeholder="Name oder E-Mail" value={historyFilters.recipient} onChange={event => setHistoryFilters(filters => ({ ...filters, recipient: event.target.value }))} /></div>
        <div className="col-md-6 col-xl-3"><label className="form-label" htmlFor="mail-filter-subject">Betreff</label><input id="mail-filter-subject" type="search" className="form-control" maxLength={100} value={historyFilters.subject} onChange={event => setHistoryFilters(filters => ({ ...filters, subject: event.target.value }))} /></div>
        <div className="col-md-4 col-xl-2"><label className="form-label" htmlFor="mail-filter-status">Status</label><select id="mail-filter-status" className="form-select" value={historyFilters.status} onChange={event => setHistoryFilters(filters => ({ ...filters, status: event.target.value }))}><option value="">Alle</option><option value="sent">Gesendet</option><option value="failed">Fehlgeschlagen</option><option value="pending">Ausstehend</option></select></div>
        <div className="col-md-4 col-xl-2"><label className="form-label" htmlFor="mail-filter-from">Von</label><input id="mail-filter-from" type="date" className="form-control" max={historyFilters.to || undefined} value={historyFilters.from} onChange={event => setHistoryFilters(filters => ({ ...filters, from: event.target.value }))} /></div>
        <div className="col-md-4 col-xl-2"><label className="form-label" htmlFor="mail-filter-to">Bis</label><input id="mail-filter-to" type="date" className="form-control" min={historyFilters.from || undefined} value={historyFilters.to} onChange={event => setHistoryFilters(filters => ({ ...filters, to: event.target.value }))} /></div>
        <div className="col-12 d-flex gap-2 flex-wrap"><button className="btn btn-primary" type="submit" disabled={historyLoading}>Filter anwenden</button><button className="btn btn-outline-secondary" type="button" onClick={resetHistoryFilters} disabled={historyLoading}>Zurücksetzen</button><span className="align-self-center text-muted small">{historyMeta.total} Treffer</span></div>
      </form>
      <div className="table-responsive"><table className="table table-striped align-middle"><thead><tr><th>Zeit</th><th>Empfänger</th><th>Betreff</th><th>Format</th><th>Status</th><th>Admin-ID</th><th>Details</th></tr></thead><tbody>{history.map(row => <tr key={row.id}><td>{row.sent_at || row.created_at}</td><td>{row.recipient_name}<br /><small>{row.recipient_email}</small></td><td>{row.subject}</td><td>{row.format}</td><td>{row.status}{row.error_message ? <small className="d-block text-danger">{row.error_message}</small> : null}</td><td>{row.sent_by}</td><td><details><summary>Anzeigen</summary><pre className="text-wrap" style={{ whiteSpace: "pre-wrap", maxWidth: 500 }}>{row.body}</pre></details></td></tr>)}{history.length === 0 && <tr><td colSpan={7}>{historyLoading ? "Lade Versandverlauf …" : "Keine E-Mails für diese Filter gefunden."}</td></tr>}</tbody></table></div>
      <nav className="d-flex align-items-center justify-content-between flex-wrap gap-2" aria-label="Seiten des Versandverlaufs"><span className="text-muted small">Seite {historyMeta.page} von {historyMeta.pages}</span><div className="d-flex gap-2"><button type="button" className="btn btn-outline-secondary btn-sm" disabled={historyLoading || historyMeta.page <= 1} onClick={() => loadHistory(appliedHistoryFilters, historyMeta.page - 1)}>Zurück</button><button type="button" className="btn btn-outline-secondary btn-sm" disabled={historyLoading || historyMeta.page >= historyMeta.pages} onClick={() => loadHistory(appliedHistoryFilters, historyMeta.page + 1)}>Weiter</button></div></nav>
    </section>}
  </main>;
}
